import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserFriendlyErrorMessage, logError, isApiError } from '../services/error-handling';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { Save, ArrowBack, Info, CheckCircle } from '@mui/icons-material';
import { useOrganization } from '../context/OrganizationContext';
import { Subscription } from '../types';
import '../components/team/utils/compatibility-fix';

const TeamCreate: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { 
    organizations, 
    getOrganizationSubscriptions, 
    assignSubscriptionToTeam,
    currentOrganizationId,
    setCurrentOrganizationId,
    createTeam,
    refreshData
  } = useOrganization();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState(orgId || currentOrganizationId || '');
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableSubscriptions, setAvailableSubscriptions] = useState<Subscription[]>([]);

  // If orgId is provided in route params, use it
  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(orgId);
      setCurrentOrganizationId(orgId);
    } else if (currentOrganizationId) {
      setSelectedOrgId(currentOrganizationId);
    }
  }, [orgId, currentOrganizationId, setCurrentOrganizationId]);

  // Update available subscriptions when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      const orgSubs = getOrganizationSubscriptions(selectedOrgId);

      // Filter to only show active subscriptions with available capacity
      const filteredSubs = orgSubs.filter(sub => {
        // Must be active
        if (sub.status !== 'active') return false;

        // Check team capacity
        const teamsLimit = sub.limits?.teamsPerOrg ?? sub.limits?.maxTeams ?? 0;
        const currentTeamsCount = sub.usage?.teamsCount ?? sub.usage?.teamsUsed ?? 0;
        const hasTeamCapacity = teamsLimit === 0 || teamsLimit > currentTeamsCount;

        // Check storage capacity
        const storageLimit = sub.limits?.maxStorageBytes ?? 0;
        const storageUsed = sub.usage?.storageUsed ?? 0;
        const hasStorageCapacity = storageLimit === 0 || storageUsed < storageLimit;

        return hasTeamCapacity && hasStorageCapacity;
      });

      setAvailableSubscriptions(filteredSubs);

      // Auto-select a subscription if there's only one
      if (filteredSubs.length === 1) {
        setSelectedSubscriptionId(filteredSubs[0]._id);
      } else if (filteredSubs.length > 0 && !selectedSubscriptionId) {
        // Otherwise select the first available subscription
        setSelectedSubscriptionId(filteredSubs[0]._id);
      } else if (filteredSubs.length === 0) {
        // Clear selection if no subscriptions are available
        setSelectedSubscriptionId('');
      }
    }
  }, [selectedOrgId, getOrganizationSubscriptions, selectedSubscriptionId]);

  const handleOrgChange = (event: SelectChangeEvent) => {
    const newOrgId = event.target.value;
    setSelectedOrgId(newOrgId);
    setCurrentOrganizationId(newOrgId);
    // Reset subscription selection when changing organization
    setSelectedSubscriptionId('');
  };

  const handleSubscriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSubscriptionId(event.target.value);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedOrgId) {
      setError('Please select an organization');
      return;
    }

    if (!selectedSubscriptionId) {
      setError('Please select a subscription plan for this team');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create the team using the new API with subscription assignment
      const newTeam = await createTeam({
        name,
        description,
        organizationId: selectedOrgId,
        subscriptionId: selectedSubscriptionId // Pass subscription directly to creation
      });
      
      // Refresh the data in context
      await refreshData();
      
      // Small delay to allow UI components to re-render with fresh data
      setTimeout(() => {
        // Navigate to the team detail page
        navigate(`/organizations/${selectedOrgId}/teams/${newTeam.id}`);
      }, 100);
    } catch (err: any) {
      // Log the error with context
      logError(err, 'Team Creation');
      
      // Check for subscription-related errors
      if (err.code === 'TEAM_LIMIT_REACHED' || 
          (err.message && err.message.includes('subscription')) || 
          (err.message && err.message.includes('maximum number of teams'))) {
        setError('You have reached the maximum number of teams allowed for your subscription plan. Please upgrade your plan to create more teams.');
      } else {
        // Get user-friendly error message for other errors
        const errorMessage = getUserFriendlyErrorMessage(err);
        setError(errorMessage);
      }
      
      // Log for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Team creation error details:', err);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Helper to get subscription usage status label and color
  const getUsageStatus = (sub: Subscription) => {
    // Handle both new and legacy field names, with proper undefined checks
    const teamsUsed = sub.usage?.teamsCount ?? sub.usage?.teamsUsed ?? 0;
    const teamsLimit = sub.limits?.teamsPerOrg ?? sub.limits?.maxTeams ?? 0;
    
    // Handle case where limits might be 0 or undefined
    if (teamsLimit === 0) {
      return { label: 'No team limit set', color: 'info' as const };
    }
    
    const percentUsed = (teamsUsed / teamsLimit) * 100;
    
    if (percentUsed >= 100) return { label: 'At capacity', color: 'error' as const };
    if (percentUsed >= 80) return { label: `${teamsUsed}/${teamsLimit} teams`, color: 'warning' as const };
    return { label: `${teamsUsed}/${teamsLimit} teams`, color: 'success' as const };
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Team
        </Typography>
        <Divider sx={{ mb: 4 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Only show organization selector if orgId is not provided in URL */}
            {!orgId && (
              <Box>
                <FormControl fullWidth required>
                  <InputLabel id="organization-select-label">Organization</InputLabel>
                  <Select
                    labelId="organization-select-label"
                    id="organization-select"
                    value={selectedOrgId}
                    label="Organization"
                    onChange={handleOrgChange}
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select the organization this team will belong to</FormHelperText>
                </FormControl>
              </Box>
            )}

            <Box>
              <TextField
                fullWidth
                required
                label="Team Name"
                value={name}
                onChange={handleNameChange}
                variant="outlined"
                helperText="Choose a name that reflects the team's purpose"
              />
            </Box>

            <Box>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                multiline
                rows={4}
                helperText="Optional: Describe the purpose and focus of this team"
              />
            </Box>

            {/* Subscription Selection */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Assign Subscription
                <Tooltip title="Teams must be assigned to a subscription plan to manage resource usage limits">
                  <Info fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
                </Tooltip>
              </Typography>
              
              {availableSubscriptions.length === 0 ? (
                <Alert severity="warning">
                  This organization does not have any active subscriptions with available capacity.
                  Please add a subscription or upgrade your current plan before creating a team.
                </Alert>
              ) : (
                <Box sx={{ width: '100%' }}>
                  {availableSubscriptions.map((sub) => {
                    const usageStatus = getUsageStatus(sub);

                    return (
                      <Box key={sub._id}>
                        <Card
                          variant="outlined"
                          sx={{
                            mb: 2,
                            borderColor: selectedSubscriptionId === sub._id ? 'primary.main' : 'divider',
                            borderWidth: selectedSubscriptionId === sub._id ? 2 : 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.light',
                              boxShadow: 1
                            }
                          }}
                          onClick={() => handleSubscriptionChange({ target: { value: sub._id } } as React.ChangeEvent<HTMLInputElement>)}
                        >
                          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Radio
                                checked={selectedSubscriptionId === sub._id}
                                value={sub._id}
                                sx={{ mt: -0.5 }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                    <Typography variant="h6" component="div" sx={{ wordBreak: 'break-word' }}>
                                      {sub.name}
                                    </Typography>
                                    {selectedSubscriptionId === sub._id && (
                                      <CheckCircle color="primary" fontSize="small" />
                                    )}
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={usageStatus.label}
                                    color={usageStatus.color}
                                    sx={{ flexShrink: 0 }}
                                  />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {sub.plan ? (sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1)) : 'Standard'} Plan
                                  {' • '}
                                  {sub.usage?.activeUsers || 0} active users
                                  {' • '}
                                  {sub.usage?.storageUsed || 0}GB storage used
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    );
                  })}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    Select a subscription plan to assign to this team. The team will share resources with other teams on the same plan.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  startIcon={<ArrowBack />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!name.trim() || !selectedOrgId || !selectedSubscriptionId || loading || availableSubscriptions.length === 0}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Creating...' : 'Create Team'}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default TeamCreate;
